import { useState } from 'react';
import { GALLERY_PHOTOS, PROJECTS } from '../lib/site';

const FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'Reforma integral', label: 'Reformas' },
  { key: 'Obra nueva', label: 'Obra nueva' },
  { key: 'Reforma comercial', label: 'Comercial' },
  { key: 'Rehabilitación', label: 'Rehabilitación' },
  { key: 'Mantenimiento', label: 'Mantenimiento' },
] as const;

const GalleryGrid = () => {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('all');
  const [lightbox, setLightbox] = useState<string | null>(null);

  const projects = filter === 'all' ? PROJECTS : PROJECTS.filter((p) => p.type === filter);
  const extraPhotos = GALLERY_PHOTOS.filter(
    (p) => !PROJECTS.some((pr) => pr.image === p)
  );

  return (
    <section className="max-w-[1400px] mx-auto px-6 py-14 md:py-20">
      {/* Filtros */}
      <div className="flex flex-wrap gap-2.5 mb-10 reveal" role="tablist" aria-label="Filtrar proyectos">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={filter === f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-[0.72rem] font-bold uppercase tracking-[0.14em] border transition-all duration-200 active:scale-95 ${
              filter === f.key
                ? 'bg-ink text-paper border-ink'
                : 'bg-transparent text-ink-soft border-line hover:border-ink hover:text-ink'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Grid editorial con alturas variadas */}
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 md:gap-6 [&>*]:mb-5 md:[&>*]:mb-6">
        {projects.map((p) => (
          <figure key={p.id} className="reveal break-inside-avoid">
            <button
              className="project-card block w-full text-left"
              onClick={() => setLightbox(p.image)}
              aria-label={`Ampliar: ${p.title}`}
            >
              <img
                src={p.image}
                alt={`${p.title} — ${p.type} de HGO Servicios en ${p.location}`}
                className="w-full object-cover"
                loading="lazy"
              />
              <figcaption className="flex items-baseline justify-between gap-3 pt-3 px-1">
                <span className="font-display font-bold text-sm tracking-tight">{p.title}</span>
                <span className="text-[0.62rem] font-bold uppercase tracking-[0.18em] text-ink-faint shrink-0">
                  {p.location} · {p.year}
                </span>
              </figcaption>
            </button>
          </figure>
        ))}
        {/* Fotos sueltas de la galería completa */}
        {filter === 'all' &&
          extraPhotos.map((photo, i) => (
            <figure key={i} className="reveal break-inside-avoid">
              <button
                className="project-card block w-full"
                onClick={() => setLightbox(photo)}
                aria-label={`Ampliar fotografía ${i + 1} de la galería`}
              >
                <img
                  src={photo}
                  alt={`Obra de HGO Servicios en Málaga — fotografía ${i + 1}`}
                  className="w-full object-cover"
                  loading="lazy"
                />
              </button>
            </figure>
          ))}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[120] bg-ink/95 backdrop-blur-sm grid place-items-center p-4 md:p-10"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-label="Imagen ampliada"
        >
          <button
            className="absolute top-5 right-5 grid place-items-center w-12 h-12 rounded-full border border-paper/30 text-paper text-xl active:scale-95 transition-transform duration-150"
            onClick={() => setLightbox(null)}
            aria-label="Cerrar"
          >
            ✕
          </button>
          <img
            src={lightbox}
            alt="Proyecto de HGO Servicios ampliado"
            className="max-h-[88vh] max-w-full object-contain rounded-[4px]"
          />
        </div>
      )}
    </section>
  );
};

export default GalleryGrid;
